import React, { useEffect, useRef } from "react";
import { size } from "lodash";
import { usePrevious } from "react-use";
import { PopoverDisclosure, usePopoverState } from "reakit/Popover";
import styled from "styled-components";
import { PeriodRange } from "@linkfluence/models/date";
import Chip from "@ui-kit/form/chip";
import Loading from "@ui-kit/loading";
import Popover from "@ui-kit/popover";
import Recap from "@radarly/components/clause/labels/labels";
import Tooltip from "@radarly/components/clause/labels/tooltip";
import { useLabelsFromClause } from "@radarly/core/clause/use-cases";
import { useFacetsFromContext } from "@radarly/core/facets/use-cases";
import { useCurrentProject } from "@radarly/core/project/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { Clause } from "@radarly/models/clause";
import { isClauseFieldFilled } from "@radarly/models/clause/is";
import { mergeClauses } from "@radarly/models/clause/merge";
import { getFacetFromField } from "@radarly/models/facets";
import Panel from "@radarly/shared/filterbar/panels";

interface IProps {
  field: string;
  clause: Clause;
  restClause: Clause;
  setClause: (clause: Clause) => void;
  onApply: () => void;
  onClose: () => void;
  onRemove: () => void;
  period: PeriodRange;
}

const StyledChip = styled(Chip)`
  margin: 0 6px;
`;

const LoaderContainer = styled.div`
  position: relative;
  width: 72px;
  padding: 3px 0 0 8px;
`;

const Loader = () => (
  <LoaderContainer>
    <Loading size="s" />
  </LoaderContainer>
);

const Root = styled.div`
  display: flex;
  align-items: center;
`;

const RadarlySharedFilterbarChip = ({
  clause,
  setClause,
  restClause,
  onApply,
  onClose,
  onRemove,
  field,
  period,
}: IProps) => {
  const focusRef = useRef<HTMLSpanElement>(null);
  const facets = useFacetsFromContext();
  const facet = getFacetFromField(facets, field);
  const locale = useCurrentUserLocale();
  const project = useCurrentProject();
  const { loading, labels } = useLabelsFromClause(
    project.id,
    clause,
    facets,
    locale,
    period
  );
  const popover = usePopoverState({ placement: "bottom-start" });
  const visible = usePrevious(popover.visible);
  useEffect(() => {
    if (visible && !popover.visible) {
      onClose();
    }
  }, [clause.and, onClose, popover.visible, visible]);
  const onSetClause = (clause: Clause) => {
    setClause(mergeClauses(clause, restClause));
  };
  const onApplyAndClose = () => {
    onApply();
    popover.hide();
  };
  return (
    <Root>
      <span ref={focusRef} />
      <Tooltip
        disabled={!labels.length}
        locale={locale}
        title={facet?.label}
        clausesLabels={labels}
      >
        <PopoverDisclosure
          {...popover}
          as={StyledChip}
          label={
            loading ? (
              <Loader />
            ) : size(labels) ? (
              <Recap clauseLabels={labels[0]} locale={locale} />
            ) : (
              facet?.label
            )
          }
          status={isClauseFieldFilled(clause, facet.id) ? "selected" : "none"}
          onClickClose={onRemove}
          size="large"
        />
      </Tooltip>
      <Popover
        popover={popover}
        ariaLabel="filter-selector"
        focusRef={focusRef}
      >
        <Panel
          clause={clause}
          setClause={onSetClause}
          onApply={onApplyAndClose}
          facet={facet}
          visible={popover.visible}
          period={period}
        />
      </Popover>
    </Root>
  );
};

export default RadarlySharedFilterbarChip;
